import React, { useEffect, useState, useRef } from 'react'
import { useDispatch } from 'react-redux'

import './AdminSideBarContent.scss'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import profile from '../../../../Assets/Images/profile-circle.png'
import { ACTIVEMENU } from '../../../../Constants/MobileMenuConstants'
import { useAuth } from '../../../../Components/AuthProvider/AuthProvider'
import { useAuth0 } from '@auth0/auth0-react'
import { ExtractLoanProvider } from 'Helpers/ExtractSubdomain'
import { LoanProviderRBAC } from 'Helpers/LoanProviderRBAC'
import authService from 'Services/authService'
import useCurrentAdmin from 'Hooks/Queries/AdminManagement/useCurrentAdmin'
import { ReactComponent as StationOperationsIcon } from 'BoostXpressAdmin/Assets/Icon/Sidebar/reconciliation.svg'

const AdminSideBarContent = () => {
  const { logout: auth0Logout } = useAuth0()

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { user, logout } = useAuth()
  const [isSwitching, setIsSwitching] = useState(false)
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Admin management access comes from the admin-auth service, not from the

  const { admin: adminAuthUser, isSuperAdmin } = useCurrentAdmin()

  function setLink(name: string) {
    dispatch({
      type: ACTIVEMENU,
      payload: {
        name,
        user: {
          loanProvider: user?.user?.loanProvider,
          loanProviderAdminRole: user?.loanProviderAdminRole,
        },
      },
    })
  }

  useEffect(() => {
    if (location.pathname.startsWith('/station-operations')) {
      setLink('Station Operations')
      return
    }

    if (location.pathname.startsWith('/admin-management')) {
      setLink('Admin Roles')
      return
    }

    if (location.pathname.startsWith('/general-overview')) {
      setLink('Overview')
      return
    }

    if (
      location.pathname === '/' ||
      location.pathname === '/dashboard' ||
      location.pathname === '/emp'
    ) {
      setLink('Dashboard')
    }
  }, [location.pathname])

  const handleLogout = () => {
    setIsProfileDropdownOpen(false)
    if (user?.user.name) {
      auth0Logout({ logoutParams: { returnTo: window.location.origin } })
      logout()
    } else {
      localStorage.removeItem('__TG')
      window.location.href = '/login'
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false)
      }
    }

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isProfileDropdownOpen])

  const [allowRoute, setAllowRoute] = useState<
    Array<{
      loanProvider: string
      adminUiSubDomain: string
      defaultAdminPageUrlPath: string
      allowedAdminPageUrlPaths: Array<string>
    }>
  >()

  const [AllowedRouteRequest, setAllowedRouteRequest] = useState(false)

  const getRoutePrivilleges = async () => {
    setAllowedRouteRequest(true)
    await authService.getPrivileges().then(
      (response) => {
        setAllowRoute(response.data.data)
        setAllowedRouteRequest(false)
      },
      (error) => {
        console.log('authService.getPrivileges', error)
      },
    )
  }

  useEffect(() => {
    getRoutePrivilleges()
  }, [])

  const loanProvider = ExtractLoanProvider()

  const routeInstance =
    AllowedRouteRequest === false && allowRoute && LoanProviderRBAC(loanProvider, allowRoute)

  return (
    <React.Fragment>
      {isSwitching && (
        <div className='tg-switching-overlay' role='status' aria-live='polite'>
          <div className='tg-switching-overlay__card'>
            <span className='spinner-border spinner-border-sm' aria-hidden='true'></span>
            <div className='tg-switching-overlay__text'>Switching, please wait...</div>
          </div>
        </div>
      )}
      <div className='asc'>
        <ul className='nav flex-column mb-4 main-nav'>
          <li className='nav-item my-1 mt-4 mb-5 ps-3 d-none d-xl-block'>
            <Link to='/dashboard'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='141'
                height='29'
                viewBox='0 0 141 29'
                fill='none'
              >
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M2.43347 6.88536L-7.24557e-07 6.88536L-8.02587e-07 5.10026L2.43347 5.10026L2.43347 0.555454L6.01964 0.555454L6.01964 5.10026L9.99004 5.10026L9.99004 6.88536L6.01964 6.88536L6.01964 13.5157C6.01964 14.1618 6.08795 14.7143 6.22456 15.1733C6.37826 15.6154 6.57464 15.9809 6.81372 16.2699C7.06987 16.5419 7.36018 16.7459 7.68465 16.8819C8.00911 17.0179 8.35065 17.0859 8.70927 17.0859C9.22158 17.0859 9.71681 17.0094 10.195 16.8564C10.6731 16.6864 11.0744 16.5079 11.3989 16.3209L12.1674 17.9785C11.655 18.2335 11.0659 18.446 10.3999 18.616C9.75096 18.786 9.10204 18.871 8.45311 18.871C6.52341 18.871 5.03771 18.395 3.99602 17.443C2.95432 16.4909 2.43347 15.1818 2.43347 13.5157L2.43347 6.88536ZM24.3317 6.88536L21.2578 6.88536C20.0966 6.88536 19.2086 7.19988 18.5938 7.82892C17.9791 8.44095 17.6717 9.359 17.6717 10.5831L17.6717 18.361L14.0855 18.361L14.0855 10.5831C14.0855 8.84898 14.632 7.5059 15.7249 6.55385C16.8349 5.58479 18.4231 5.10026 20.4894 5.10026L24.3317 5.10026L24.3317 6.88536ZM31.487 16.5759C29.5232 16.5759 28.5413 15.7684 28.5413 14.1533C28.5413 12.5382 29.5232 11.7306 31.487 11.7306L35.0732 11.7306L35.0732 16.5759L31.487 16.5759ZM34.0998 7.24238C34.7487 7.82042 35.0732 8.72147 35.0732 9.94554L31.487 9.94554C29.1646 9.94554 27.4483 10.2941 26.3383 10.9911C25.2454 11.6881 24.6989 12.7422 24.6989 14.1533C24.6989 15.5644 25.2454 16.6184 26.3383 17.3155C27.4483 18.0125 29.1646 18.361 31.487 18.361L38.6594 18.361L38.6594 9.94554C38.6594 9.14649 38.5057 8.42395 38.1983 7.77792C37.908 7.11488 37.4725 6.54535 36.8919 6.06932C36.3113 5.59329 35.6026 5.22777 34.7658 4.97276C33.929 4.71774 32.9642 4.59024 31.8713 4.59024C31.4614 4.59024 31.0174 4.62424 30.5393 4.69224C30.0782 4.76024 29.6086 4.86225 29.1304 4.99826C28.6523 5.11726 28.1826 5.27027 27.7216 5.45728C27.2776 5.64429 26.8677 5.86531 26.492 6.12032L27.3886 7.65041C27.9692 7.25938 28.6266 6.95337 29.3609 6.73236C30.0953 6.49434 30.8466 6.37534 31.6151 6.37534C32.6397 6.37534 33.468 6.66435 34.0998 7.24238ZM52.4797 6.88536L49.2778 6.88536C48.6972 6.88536 48.1592 6.97887 47.664 7.16588C47.1859 7.33589 46.7675 7.61641 46.4089 8.00743C46.0673 8.39845 45.7941 8.89998 45.5892 9.51201C45.4013 10.124 45.3074 10.8636 45.3074 11.7306C45.3074 12.5977 45.4013 13.3372 45.5892 13.9493C45.7941 14.5613 46.0673 15.0628 46.4089 15.4538C46.7675 15.8449 47.1859 16.1339 47.664 16.3209C48.1592 16.4909 48.6972 16.5759 49.2778 16.5759L52.4797 16.5759L52.4797 6.88536ZM49.2778 18.361C48.0995 18.361 47.0236 18.208 46.0502 17.902C45.0939 17.596 44.2742 17.1539 43.5912 16.5759C42.9081 15.9979 42.3787 15.3008 42.003 14.4848C41.6444 13.6687 41.4651 12.7507 41.4651 11.7306C41.4651 10.7106 41.6444 9.79253 42.003 8.97648C42.3787 8.16044 42.9081 7.4634 43.5912 6.88536C44.2742 6.30733 45.0939 5.86531 46.0502 5.55929C47.0236 5.25327 48.0995 5.10026 49.2778 5.10026L52.4797 5.10026L52.4797 0.510006L56.0659 0.510005L56.0659 18.361L49.2778 18.361ZM69.6567 16.6524C68.9395 16.9414 68.1625 17.0859 67.3257 17.0859C66.1474 17.0859 65.1655 16.7799 64.3799 16.1679C63.6114 15.5559 63.1248 14.5443 62.9198 13.1332L73.8576 13.1332L73.8576 11.3481C73.8576 10.2941 73.6613 9.35051 73.2685 8.51746C72.8757 7.68441 72.3463 6.97887 71.6803 6.40084C71.0143 5.8058 70.2288 5.35528 69.3237 5.04926C68.4186 4.74324 67.4538 4.59024 66.4292 4.59024C65.3362 4.59024 64.3372 4.75174 63.4321 5.07476C62.5271 5.38078 61.7415 5.83981 61.0755 6.45184C60.4266 7.04687 59.9143 7.79492 59.5386 8.69597C59.18 9.58002 59.0007 10.5916 59.0007 11.7306C59.0007 12.8867 59.1885 13.9068 59.5642 14.7908C59.957 15.6749 60.5034 16.4229 61.2036 17.0349C61.9038 17.63 62.7491 18.0805 63.7395 18.3865C64.7471 18.7095 65.8571 18.871 67.0695 18.871C67.616 18.871 68.1625 18.82 68.7089 18.718C69.2554 18.633 69.7762 18.514 70.2715 18.361C70.7838 18.191 71.2534 17.987 71.6803 17.749C72.1243 17.511 72.5086 17.2474 72.833 16.9584L71.8084 15.4283C71.1082 15.9554 70.391 16.3634 69.6567 16.6524ZM65.0459 6.52834C65.4728 6.34133 65.9339 6.24783 66.4292 6.24783C66.8732 6.24783 67.3086 6.34983 67.7355 6.55385C68.1795 6.74086 68.5638 7.04687 68.8882 7.4719C69.2298 7.87992 69.503 8.40695 69.7079 9.05299C69.9129 9.69902 70.0153 10.4641 70.0153 11.3481L62.843 11.3481C62.8601 10.4301 62.9625 9.64802 63.1504 9.00198C63.3553 8.35595 63.6114 7.82892 63.9188 7.4209C64.2433 7.01287 64.619 6.71536 65.0459 6.52834ZM83.9738 6.88536C82.7442 6.88536 81.7708 7.26789 81.0536 8.03293C80.3534 8.78097 80.0034 9.92854 80.0034 11.4756C80.0034 13.0227 80.3534 14.1788 81.0536 14.9438C81.7708 15.6919 82.7442 16.0659 83.9738 16.0659L87.1757 16.0659L87.1757 6.88536L83.9738 6.88536ZM90.7619 18.106C90.7619 18.905 90.6082 19.6276 90.3008 20.2736C89.9934 20.9367 89.5323 21.5062 88.9176 21.9822C88.3199 22.4582 87.5599 22.8238 86.6378 23.0788C85.7327 23.3338 84.6739 23.4613 83.4614 23.4613C83.0516 23.4613 82.6076 23.4273 82.1294 23.3593C81.6684 23.3083 81.1988 23.2148 80.7206 23.0788C80.2424 22.9598 79.7728 22.7983 79.3117 22.5942C78.8677 22.4072 78.4579 22.1862 78.0822 21.9312L78.9787 20.4011C79.5594 20.7921 80.2168 21.0982 80.9511 21.3192C81.6854 21.5572 82.4368 21.6762 83.2053 21.6762C84.4861 21.6762 85.468 21.3872 86.1511 20.8091C86.8342 20.2311 87.1757 19.3301 87.1757 18.106L87.1757 17.851L83.9738 17.851C82.7954 17.851 81.7196 17.698 80.7462 17.392C79.7899 17.0859 78.9702 16.6524 78.2871 16.0914C77.604 15.5304 77.0746 14.8588 76.699 14.0768C76.3403 13.2947 76.161 12.4277 76.161 11.4756C76.161 10.5236 76.3403 9.65652 76.699 8.87448C77.0747 8.09243 77.604 7.4209 78.2871 6.85986C78.9702 6.29883 79.7899 5.86531 80.7462 5.55929C81.7196 5.25327 82.7954 5.10026 83.9738 5.10026L90.7619 5.10026L90.7619 18.106ZM101.381 6.88536L104.455 6.88536L104.455 5.10026L100.613 5.10026C98.5465 5.10026 96.9584 5.58479 95.8484 6.55385C94.7554 7.5059 94.209 8.84897 94.209 10.5831L94.209 18.361L97.7951 18.361L97.7951 10.5831C97.7951 9.359 98.1025 8.44095 98.7173 7.82892C99.3321 7.19988 100.22 6.88536 101.381 6.88536ZM110.209 18.361L106.622 18.361L106.622 5.35528L110.209 5.35528L110.209 18.361ZM110.337 1.91259C110.337 2.42261 110.157 2.87314 109.799 3.26416C109.457 3.63818 108.996 3.82519 108.416 3.82519C107.835 3.82519 107.365 3.63818 107.007 3.26416C106.665 2.87314 106.494 2.42261 106.494 1.91258C106.494 1.40256 106.665 0.960532 107.007 0.586511C107.365 0.195489 107.835 -2.37871e-05 108.416 -2.38125e-05C108.979 -2.38371e-05 109.44 0.195489 109.799 0.586511C110.157 0.960532 110.337 1.40256 110.337 1.91259ZM121.096 6.88536L124.298 6.88536L124.298 16.5759L121.096 16.5759C120.516 16.5759 119.978 16.4909 119.482 16.3209C119.004 16.1339 118.586 15.8449 118.227 15.4538C117.886 15.0628 117.612 14.5613 117.408 13.9493C117.22 13.3372 117.126 12.5977 117.126 11.7306C117.126 10.8636 117.22 10.124 117.408 9.51201C117.613 8.89998 117.886 8.39845 118.227 8.00743C118.586 7.61641 119.004 7.33589 119.482 7.16588C119.978 6.97887 120.516 6.88536 121.096 6.88536ZM117.869 17.902C118.842 18.208 119.918 18.361 121.096 18.361L127.884 18.361L127.884 0.510006L124.298 0.510006L124.298 5.10026L121.096 5.10026C119.918 5.10026 118.842 5.25327 117.869 5.55929C116.912 5.86531 116.093 6.30733 115.41 6.88537C114.726 7.4634 114.197 8.16044 113.821 8.97648C113.463 9.79253 113.283 10.7106 113.283 11.7306C113.283 12.7507 113.463 13.6687 113.821 14.4848C114.197 15.3008 114.726 15.9979 115.41 16.5759C116.093 17.1539 116.912 17.596 117.869 17.902ZM133.529 1.04022L133.529 5.34266L134.167 5.34266L134.167 1.04022L135.762 1.04022L135.762 0.464569L131.941 0.464569L131.941 1.04022L133.529 1.04022ZM136.4 0.464569L136.4 5.34266L137.015 5.34266L137.015 1.18996L138.409 5.34266L138.984 5.34266L140.385 1.25984L140.385 5.34266L141 5.34266L141 0.464569L140.142 0.464569L138.988 3.85861C138.871 4.20245 138.786 4.4631 138.731 4.64057C138.683 4.48085 138.606 4.24016 138.501 3.9185L137.36 0.464569L136.4 0.464569Z'
                  fill='white'
                />
                <rect
                  width='38.9373'
                  height='4.12971'
                  transform='matrix(1 0 0 -1 0 28.318)'
                  fill='#E873A8'
                />
              </svg>
            </Link>
          </li>

          {!AllowedRouteRequest && routeInstance && (
            <div className='scroll-vertical'>
              {routeInstance.privileges('dashboard') && (
                <>
                  <div className='section-title mt-0'>OVERVIEW</div>
                  <li className='nav-item mt-3'>
                    <NavLink
                      className={({ isActive }) => {
                        return (isActive && location.pathname === '/dashboard') ||
                          location.pathname === '/emp'
                          ? 'nav-link active'
                          : 'nav-link'
                      }}
                      to='/dashboard'
                      onClick={() => setLink('Dashboard')}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                      >
                        <path
                          opacity='0.4'
                          fillRule='evenodd'
                          clipRule='evenodd'
                          d='M3.13978 8.37009L10.0698 2.8201C11.1298 1.9701 12.8598 1.97008 13.9298 2.83008L20.8598 8.37009C21.6298 8.99009 22.1298 10.3001 21.9698 11.2801L20.6398 19.2401C20.3998 20.6501 19.0298 21.8101 17.5998 21.8101H15V13C15 11.8954 14.1046 11 13 11H11C9.89543 11 9 11.8954 9 13V21.8101H6.39977C4.95977 21.8101 3.59978 20.6601 3.35978 19.2401L2.02978 11.2801C1.85978 10.3001 2.35978 8.99009 3.13978 8.37009Z'
                          fill='#E873A8'
                        />
                        <path
                          d='M9 13C9 11.8954 9.89543 11 11 11H13C14.1046 11 15 11.8954 15 13V22H9V13Z'
                          fill='#E873A8'
                        />
                      </svg>
                      <span>Dashboard</span>
                    </NavLink>
                  </li>

                  <li className='nav-item mt-3'>
                    <NavLink
                      className={({ isActive }) => {
                        return (isActive && location.pathname === '/general-overview-v2') ||
                          location.pathname === '/emp'
                          ? 'nav-link active'
                          : 'nav-link'
                      }}
                      to='/general-overview-v2'
                      onClick={() => setLink('Overview')}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                      >
                        <path
                          opacity='0.4'
                          fillRule='evenodd'
                          clipRule='evenodd'
                          d='M3.13978 8.37009L10.0698 2.8201C11.1298 1.9701 12.8598 1.97008 13.9298 2.83008L20.8598 8.37009C21.6298 8.99009 22.1298 10.3001 21.9698 11.2801L20.6398 19.2401C20.3998 20.6501 19.0298 21.8101 17.5998 21.8101H15V13C15 11.8954 14.1046 11 13 11H11C9.89543 11 9 11.8954 9 13V21.8101H6.39977C4.95977 21.8101 3.59978 20.6601 3.35978 19.2401L2.02978 11.2801C1.85978 10.3001 2.35978 8.99009 3.13978 8.37009Z'
                          fill='#E873A8'
                        />
                        <path
                          d='M9 13C9 11.8954 9.89543 11 11 11H13C14.1046 11 15 11.8954 15 13V22H9V13Z'
                          fill='#E873A8'
                        />
                      </svg>
                      <span>General Overview</span>
                    </NavLink>
                  </li>

                  {/* <li className='nav-item nav-sub-item'>
                    <NavLink
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                      to='/general-overview'
                      onClick={() => setLink('Overview')}
                    >
                      <span>General Overview</span>
                    </NavLink>
                  </li> */}
                </>
              )}

              <div className='section-title'>TradeGrid Products</div>
              <li className='nav-item'>
                <button
                  type='button'
                  className='nav-link border-0 bg-transparent text-start'
                  onClick={() => {
                    setIsSwitching(true)
                    window.setTimeout(() => {
                      navigate('/blockchain', { replace: true })
                    }, 450)
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <path
                      opacity='0.4'
                      d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                      fill='#516F93'
                    />
                    <path
                      d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                      fill='#516F93'
                    />
                  </svg>
                  <span>TDLA </span>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  type='button'
                  className='nav-link border-0 bg-transparent text-start'
                  onClick={() => {
                    setIsSwitching(true)
                    window.setTimeout(() => {
                      navigate('/boostxpress', { replace: true })
                    }, 450)
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <path
                      opacity='0.4'
                      d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                      fill='#516F93'
                    />
                    <path
                      d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                      fill='#516F93'
                    />
                  </svg>
                  <span>BoostXpress</span>
                </button>
              </li>

              {routeInstance.privileges('plp-application') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/plp?tab=applications'
                    onClick={() => setLink('Traders Card')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <g opacity='0.4'>
                        <path
                          d='M21.6377 12.0235C21.4274 10.3317 20.0191 9.08414 18.3375 9.08414H16.0252C14.3647 9.08414 12.9352 10.353 12.7461 12.0235L12.4309 14.7302C12.3888 15.0897 12.599 15.407 12.9564 15.4915C13.2716 15.5549 13.587 15.6184 13.9234 15.6607L16.3827 13.7998C16.677 13.5882 17.0133 13.4614 17.3916 13.4614C18.1063 13.4614 18.7369 13.9266 18.9891 14.6032L19.3885 15.8086C20.1032 15.7241 20.7969 15.6394 21.4696 15.4915C21.8059 15.407 22.0371 15.0897 21.9951 14.7302L21.6377 12.0235Z'
                          fill='#516F93'
                        />
                        <path
                          d='M20.3134 5.12978C20.3134 6.85823 18.9205 8.25941 17.2023 8.25941C15.4842 8.25941 14.0914 6.85827 14.0914 5.12978C14.0914 3.40133 15.4841 2 17.2023 2C18.9204 2 20.3134 3.40129 20.3134 5.12978Z'
                          fill='#516F93'
                        />
                        <path
                          d='M7.93269 15.1485H5.6204C3.93876 15.1485 2.53046 16.4174 2.32018 18.0878L2.00494 20.7946C1.96286 21.1541 2.19409 21.4713 2.53044 21.5559C3.87572 21.8519 5.28403 22 6.77649 22C8.24786 22 9.6773 21.8519 11.0225 21.5559C11.3589 21.4713 11.5901 21.154 11.548 20.7946L11.2328 18.0878C11.0224 16.3961 9.61418 15.1485 7.93269 15.1485Z'
                          fill='#516F93'
                        />
                        <path
                          d='M9.88748 11.1941C9.88748 12.9226 8.49471 14.3238 6.77655 14.3238C5.05842 14.3238 3.66547 12.9226 3.66547 11.1941C3.66547 9.46565 5.05839 8.06436 6.77655 8.06436C8.49468 8.06436 9.88748 9.46565 9.88748 11.1941Z'
                          fill='#516F93'
                        />
                      </g>
                      <path
                        d='M17.9303 15.2929C17.8463 15.018 17.5099 14.9124 17.2787 15.0814L14.462 17.2173C14.1887 17.4288 14.2518 17.8728 14.5881 17.9785L15.3869 18.2322C14.9454 19.5011 14.1257 20.5161 13.0747 21.2139C12.9486 21.2984 12.9906 21.5311 13.1377 21.5523C15.0925 21.8907 17.0264 20.8121 17.7201 18.9301L18.5399 19.1838C18.8762 19.2896 19.1914 18.9723 19.0654 18.6341L17.9303 15.2929Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Traders Card</span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('tgplus-subscription') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/tgplus-subscription'
                    onClick={() => setLink('Tradegrid Plus Subscription')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>TG Plus</span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('station-operations') && (
                <li className='nav-item'>
                  <button
                    type='button'
                    className='nav-link border-0 bg-transparent text-start'
                    onClick={() => {
                      setIsSwitching(true)
                      window.setTimeout(() => {
                        navigate('/station-operations', { replace: true })
                      }, 450)
                    }}
                  >
                    <StationOperationsIcon aria-hidden='true' focusable='false' />
                    <span>Station Operations</span>
                  </button>
                </li>
              )}

              {(routeInstance.privileges('company') ||
                routeInstance.privileges('products') ||
                routeInstance.privileges('orders')) && (
                  <div className='section-title'>MANAGEMENT</div>
                )}
              {routeInstance.privileges('company') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/company'
                    onClick={() => setLink('Company')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <g opacity='0.4'>
                        <path
                          d='M21.6377 12.0235C21.4274 10.3317 20.0191 9.08414 18.3375 9.08414H16.0252C14.3647 9.08414 12.9352 10.353 12.7461 12.0235L12.4309 14.7302C12.3888 15.0897 12.599 15.407 12.9564 15.4915C13.2716 15.5549 13.587 15.6184 13.9234 15.6607L16.3827 13.7998C16.677 13.5882 17.0133 13.4614 17.3916 13.4614C18.1063 13.4614 18.7369 13.9266 18.9891 14.6032L19.3885 15.8086C20.1032 15.7241 20.7969 15.6394 21.4696 15.4915C21.8059 15.407 22.0371 15.0897 21.9951 14.7302L21.6377 12.0235Z'
                          fill='#516F93'
                        />
                        <path
                          d='M20.3134 5.12978C20.3134 6.85823 18.9205 8.25941 17.2023 8.25941C15.4842 8.25941 14.0914 6.85827 14.0914 5.12978C14.0914 3.40133 15.4841 2 17.2023 2C18.9204 2 20.3134 3.40129 20.3134 5.12978Z'
                          fill='#516F93'
                        />
                        <path
                          d='M7.93269 15.1485H5.6204C3.93876 15.1485 2.53046 16.4174 2.32018 18.0878L2.00494 20.7946C1.96286 21.1541 2.19409 21.4713 2.53044 21.5559C3.87572 21.8519 5.28403 22 6.77649 22C8.24786 22 9.6773 21.8519 11.0225 21.5559C11.3589 21.4713 11.5901 21.154 11.548 20.7946L11.2328 18.0878C11.0224 16.3961 9.61418 15.1485 7.93269 15.1485Z'
                          fill='#516F93'
                        />
                        <path
                          d='M9.88748 11.1941C9.88748 12.9226 8.49471 14.3238 6.77655 14.3238C5.05842 14.3238 3.66547 12.9226 3.66547 11.1941C3.66547 9.46565 5.05839 8.06436 6.77655 8.06436C8.49468 8.06436 9.88748 9.46565 9.88748 11.1941Z'
                          fill='#516F93'
                        />
                      </g>
                      <path
                        d='M17.9303 15.2929C17.8463 15.018 17.5099 14.9124 17.2787 15.0814L14.462 17.2173C14.1887 17.4288 14.2518 17.8728 14.5881 17.9785L15.3869 18.2322C14.9454 19.5011 14.1257 20.5161 13.0747 21.2139C12.9486 21.2984 12.9906 21.5311 13.1377 21.5523C15.0925 21.8907 17.0264 20.8121 17.7201 18.9301L18.5399 19.1838C18.8762 19.2896 19.1914 18.9723 19.0654 18.6341L17.9303 15.2929Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Company</span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('products') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/products'
                    onClick={() => setLink('Products')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M21.0407 7.19L12.0007 12.42L2.96069 7.19C3.36069 6.45 3.94069 5.8 4.59069 5.44L9.93069 2.48C11.0707 1.84 12.9307 1.84 14.0707 2.48L19.4107 5.44C20.0607 5.8 20.6407 6.45 21.0407 7.19Z'
                        fill='#516F93'
                      />
                      <path
                        opacity='0.6'
                        d='M12.0006 12.4199V21.9999C11.2506 21.9999 10.5006 21.8399 9.93062 21.5199L4.59063 18.5599C3.38063 17.8899 2.39062 16.2099 2.39062 14.8299V9.16994C2.39062 8.52994 2.61062 7.82994 2.96062 7.18994L12.0006 12.4199Z'
                        fill='#516F93'
                      />
                      <path
                        d='M21.6107 9.16994V14.8299C21.6107 16.2099 20.6207 17.8899 19.4107 18.5599L14.0707 21.5199C13.5007 21.8399 12.7507 21.9999 12.0007 21.9999V12.4199L21.0407 7.18994C21.3907 7.82994 21.6107 8.52994 21.6107 9.16994Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Products</span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('orders') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/orders'
                    onClick={() => setLink('Orders')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M20.5 10.19H17.61C15.24 10.19 13.31 8.26 13.31 5.89V3C13.31 2.45 12.86 2 12.31 2H8.07C4.99 2 2.5 4 2.5 7.57V16.43C2.5 20 4.99 22 8.07 22H15.93C19.01 22 21.5 20 21.5 16.43V11.19C21.5 10.64 21.05 10.19 20.5 10.19Z'
                        fill='#516F93'
                      />
                      <path
                        d='M15.7999 2.20999C15.3899 1.79999 14.6799 2.07999 14.6799 2.64999V6.13999C14.6799 7.59999 15.9199 8.80999 17.4299 8.80999C18.3799 8.81999 19.6999 8.81999 20.8299 8.81999C21.3999 8.81999 21.6999 8.14999 21.2999 7.74999C19.8599 6.29999 17.2799 3.68999 15.7999 2.20999Z'
                        fill='#516F93'
                      />
                      <path
                        d='M13.5 13.75H7.5C7.09 13.75 6.75 13.41 6.75 13C6.75 12.59 7.09 12.25 7.5 12.25H13.5C13.91 12.25 14.25 12.59 14.25 13C14.25 13.41 13.91 13.75 13.5 13.75Z'
                        fill='#516F93'
                      />
                      <path
                        d='M11.5 17.75H7.5C7.09 17.75 6.75 17.41 6.75 17C6.75 16.59 7.09 16.25 7.5 16.25H11.5C11.91 16.25 12.25 16.59 12.25 17C12.25 17.41 11.91 17.75 11.5 17.75Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Orders</span>
                  </NavLink>
                </li>
              )}

              {/* {routeInstance.privileges('finance') && ( */}
              <li className='nav-item'>
                <button
                  type='button'
                  className='nav-link border-0 bg-transparent text-start'
                  onClick={() => {
                    navigate('/finance', { replace: true })
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <path
                      opacity='0.4'
                      d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                      fill='#516F93'
                    />
                    <path
                      d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                      fill='#516F93'
                    />
                  </svg>
                  <span>Finance</span>
                </button>
              </li>
              {/* )} */}

              {routeInstance.privileges('request') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/requests'
                    onClick={() => setLink('Requests')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M20.5 10.19H17.61C15.24 10.19 13.31 8.26 13.31 5.89V3C13.31 2.45 12.86 2 12.31 2H8.07C4.99 2 2.5 4 2.5 7.57V16.43C2.5 20 4.99 22 8.07 22H15.93C19.01 22 21.5 20 21.5 16.43V11.19C21.5 10.64 21.05 10.19 20.5 10.19Z'
                        fill='#516F93'
                      />
                      <path
                        d='M15.7999 2.20999C15.3899 1.79999 14.6799 2.07999 14.6799 2.64999V6.13999C14.6799 7.59999 15.9199 8.80999 17.4299 8.80999C18.3799 8.81999 19.6999 8.81999 20.8299 8.81999C21.3999 8.81999 21.6999 8.14999 21.2999 7.74999C19.8599 6.29999 17.2799 3.68999 15.7999 2.20999Z'
                        fill='#516F93'
                      />
                      <path
                        d='M13.5 13.75H7.5C7.09 13.75 6.75 13.41 6.75 13C6.75 12.59 7.09 12.25 7.5 12.25H13.5C13.91 12.25 14.25 12.59 14.25 13C14.25 13.41 13.91 13.75 13.5 13.75Z'
                        fill='#516F93'
                      />
                      <path
                        d='M11.5 17.75H7.5C7.09 17.75 6.75 17.41 6.75 17C6.75 16.59 7.09 16.25 7.5 16.25H11.5C11.91 16.25 12.25 16.59 12.25 17C12.25 17.41 11.91 17.75 11.5 17.75Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Requests</span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('transaction') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/transactions'
                    onClick={() => setLink('Transactions')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M20.5 10.19H17.61C15.24 10.19 13.31 8.26 13.31 5.89V3C13.31 2.45 12.86 2 12.31 2H8.07C4.99 2 2.5 4 2.5 7.57V16.43C2.5 20 4.99 22 8.07 22H15.93C19.01 22 21.5 20 21.5 16.43V11.19C21.5 10.64 21.05 10.19 20.5 10.19Z'
                        fill='#516F93'
                      />
                      <path
                        d='M15.7999 2.20999C15.3899 1.79999 14.6799 2.07999 14.6799 2.64999V6.13999C14.6799 7.59999 15.9199 8.80999 17.4299 8.80999C18.3799 8.81999 19.6999 8.81999 20.8299 8.81999C21.3999 8.81999 21.6999 8.14999 21.2999 7.74999C19.8599 6.29999 17.2799 3.68999 15.7999 2.20999Z'
                        fill='#516F93'
                      />
                      <path
                        d='M13.5 13.75H7.5C7.09 13.75 6.75 13.41 6.75 13C6.75 12.59 7.09 12.25 7.5 12.25H13.5C13.91 12.25 14.25 12.59 14.25 13C14.25 13.41 13.91 13.75 13.5 13.75Z'
                        fill='#516F93'
                      />
                      <path
                        d='M11.5 17.75H7.5C7.09 17.75 6.75 17.41 6.75 17C6.75 16.59 7.09 16.25 7.5 16.25H11.5C11.91 16.25 12.25 16.59 12.25 17C12.25 17.41 11.91 17.75 11.5 17.75Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Transactions</span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('logistics') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/logistics'
                    onClick={() => setLink('Logistics')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M20.5 10.19H17.61C15.24 10.19 13.31 8.26 13.31 5.89V3C13.31 2.45 12.86 2 12.31 2H8.07C4.99 2 2.5 4 2.5 7.57V16.43C2.5 20 4.99 22 8.07 22H15.93C19.01 22 21.5 20 21.5 16.43V11.19C21.5 10.64 21.05 10.19 20.5 10.19Z'
                        fill='#516F93'
                      />
                      <path
                        d='M15.7999 2.20999C15.3899 1.79999 14.6799 2.07999 14.6799 2.64999V6.13999C14.6799 7.59999 15.9199 8.80999 17.4299 8.80999C18.3799 8.81999 19.6999 8.81999 20.8299 8.81999C21.3999 8.81999 21.6999 8.14999 21.2999 7.74999C19.8599 6.29999 17.2799 3.68999 15.7999 2.20999Z'
                        fill='#516F93'
                      />
                      <path
                        d='M13.5 13.75H7.5C7.09 13.75 6.75 13.41 6.75 13C6.75 12.59 7.09 12.25 7.5 12.25H13.5C13.91 12.25 14.25 12.59 14.25 13C14.25 13.41 13.91 13.75 13.5 13.75Z'
                        fill='#516F93'
                      />
                      <path
                        d='M11.5 17.75H7.5C7.09 17.75 6.75 17.41 6.75 17C6.75 16.59 7.09 16.25 7.5 16.25H11.5C11.91 16.25 12.25 16.59 12.25 17C12.25 17.41 11.91 17.75 11.5 17.75Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Logistics</span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('users') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/users'
                    onClick={() => setLink('Users')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <g opacity='0.4'>
                        <path
                          d='M21.6377 12.0235C21.4274 10.3317 20.0191 9.08414 18.3375 9.08414H16.0252C14.3647 9.08414 12.9352 10.353 12.7461 12.0235L12.4309 14.7302C12.3888 15.0897 12.599 15.407 12.9564 15.4915C13.2716 15.5549 13.587 15.6184 13.9234 15.6607L16.3827 13.7998C16.677 13.5882 17.0133 13.4614 17.3916 13.4614C18.1063 13.4614 18.7369 13.9266 18.9891 14.6032L19.3885 15.8086C20.1032 15.7241 20.7969 15.6394 21.4696 15.4915C21.8059 15.407 22.0371 15.0897 21.9951 14.7302L21.6377 12.0235Z'
                          fill='#516F93'
                        />
                        <path
                          d='M20.3134 5.12978C20.3134 6.85823 18.9205 8.25941 17.2023 8.25941C15.4842 8.25941 14.0914 6.85827 14.0914 5.12978C14.0914 3.40133 15.4841 2 17.2023 2C18.9204 2 20.3134 3.40129 20.3134 5.12978Z'
                          fill='#516F93'
                        />
                        <path
                          d='M7.93269 15.1485H5.6204C3.93876 15.1485 2.53046 16.4174 2.32018 18.0878L2.00494 20.7946C1.96286 21.1541 2.19409 21.4713 2.53044 21.5559C3.87572 21.8519 5.28403 22 6.77649 22C8.24786 22 9.6773 21.8519 11.0225 21.5559C11.3589 21.4713 11.5901 21.154 11.548 20.7946L11.2328 18.0878C11.0224 16.3961 9.61418 15.1485 7.93269 15.1485Z'
                          fill='#516F93'
                        />
                        <path
                          d='M9.88748 11.1941C9.88748 12.9226 8.49471 14.3238 6.77655 14.3238C5.05842 14.3238 3.66547 12.9226 3.66547 11.1941C3.66547 9.46565 5.05839 8.06436 6.77655 8.06436C8.49468 8.06436 9.88748 9.46565 9.88748 11.1941Z'
                          fill='#516F93'
                        />
                      </g>
                      <path
                        d='M17.9303 15.2929C17.8463 15.018 17.5099 14.9124 17.2787 15.0814L14.462 17.2173C14.1887 17.4288 14.2518 17.8728 14.5881 17.9785L15.3869 18.2322C14.9454 19.5011 14.1257 20.5161 13.0747 21.2139C12.9486 21.2984 12.9906 21.5311 13.1377 21.5523C15.0925 21.8907 17.0264 20.8121 17.7201 18.9301L18.5399 19.1838C18.8762 19.2896 19.1914 18.9723 19.0654 18.6341L17.9303 15.2929Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Users</span>
                  </NavLink>
                </li>
              )}

              {(routeInstance.privileges('settings') ||
                routeInstance.privileges('tgplus-subscription')) && (
                  <div className='section-title'>SYSTEM & SETTINGS</div>
                )}
              {routeInstance.privileges('settings') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/city-launchers'
                    onClick={() => setLink('City Launchers')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>City Launchers</span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('settings') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/web-control/announcement'
                    onClick={() => setLink('Web Control')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Website Control</span>
                  </NavLink>
                </li>
              )}

              {adminAuthUser && (
                <>
                  <div className='section-title'>ACCESS & SECURITY</div>
                  <li className='nav-item'>
                    <NavLink
                      className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                      to='/admin-management'
                      end
                      onClick={() => setLink('Admin Roles')}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                      >
                        <path
                          opacity='0.4'
                          d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                          fill='#516F93'
                        />
                        <path
                          d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                          fill='#516F93'
                        />
                      </svg>
                      <span>Administrators</span>
                    </NavLink>
                  </li>

                  {/* Role CRUD is SUPER_ADMIN-only on the API. */}
                  {isSuperAdmin && (
                    <li className='nav-item'>
                      <NavLink
                        className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                        to='/admin-management/roles'
                        onClick={() => setLink('Admin Roles')}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='24'
                          height='24'
                          viewBox='0 0 24 24'
                          fill='none'
                        >
                          <path
                            opacity='0.4'
                            d='M12 2L4 5.09V11.5C4 16.19 7.41 20.44 12 21.55C16.59 20.44 20 16.19 20 11.5V5.09L12 2Z'
                            fill='#516F93'
                          />
                          <path
                            d='M11.25 14.42L9.02 12.19C8.73 11.9 8.73 11.43 9.02 11.13C9.31 10.84 9.79 10.84 10.08 11.13L11.25 12.3L14.42 9.13C14.71 8.84 15.19 8.84 15.48 9.13C15.77 9.43 15.77 9.9 15.48 10.19L11.25 14.42Z'
                            fill='#516F93'
                          />
                        </svg>
                        <span>Admin Roles</span>
                      </NavLink>
                    </li>
                  )}
                </>
              )}

              {(routeInstance.privileges('lease-assets') ||
                routeInstance.privileges('filling-stations') ||
                routeInstance.privileges('loan-providers') ||
                user?.loanProviderAdminRole) && (
                  <div className='section-title'>ASSETS & PARTNERS</div>
                )}
              {/* {routeInstance.privileges('lease-assets') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/lease-assets'
                    onClick={() => setLink('Lease Assets')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Lease Assets</span>
                  </NavLink>
                </li>
              )} */}

              {/* {routeInstance.privileges('filling-stations') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/filling-stations'
                    onClick={() => setLink('Filling Stations')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Filling Stations</span>
                  </NavLink>
                </li>
              )} */}

              {routeInstance.privileges('settings') && (
                <li className='nav-item d-none'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/settings'
                    onClick={() => setLink('Settings')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Settings</span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('bulksms') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/bulksms'
                    onClick={() => setLink('Bulk SMS')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Bulk SMS</span>
                  </NavLink>
                </li>
              )}
              {routeInstance.privileges('ultra-delivery-fee') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/ultra-delivery'
                    onClick={() => setLink('Ultra Delivery Fee')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Ultra Delivery Fee </span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('loan-providers') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to='/loan-providers'
                    onClick={() => setLink('Loan Providers')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Loan Providers </span>
                  </NavLink>
                </li>
              )}

              {user?.loanProviderAdminRole && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to={`/loan-providers/users/${user?.user?.loanProvider}`}
                    onClick={() => setLink('Users')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Users</span>
                  </NavLink>
                </li>
              )}

              {user?.loanProviderAdminRole && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to={`/loan-providers/transaction/${user?.user?.loanProvider}`}
                    onClick={() => setLink('Transactions')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Transactions</span>
                  </NavLink>
                </li>
              )}

              {routeInstance.privileges('users') && (
                <li className='nav-item'>
                  <NavLink
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    to={'/paystack-statement'}
                    onClick={() => setLink('Paystack Settlement')}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                    >
                      <path
                        opacity='0.4'
                        d='M2 12.8801V11.1201C2 10.0801 2.85 9.22006 3.9 9.22006C5.71 9.22006 6.45 7.94006 5.54 6.37006C5.02 5.47006 5.33 4.30006 6.24 3.78006L7.97 2.79006C8.76 2.32006 9.78 2.60006 10.25 3.39006L10.36 3.58006C11.26 5.15006 12.74 5.15006 13.65 3.58006L13.76 3.39006C14.23 2.60006 15.25 2.32006 16.04 2.79006L17.77 3.78006C18.68 4.30006 18.99 5.47006 18.47 6.37006C17.56 7.94006 18.3 9.22006 20.11 9.22006C21.15 9.22006 22.01 10.0701 22.01 11.1201V12.8801C22.01 13.9201 21.16 14.7801 20.11 14.7801C18.3 14.7801 17.56 16.0601 18.47 17.6301C18.99 18.5401 18.68 19.7001 17.77 20.2201L16.04 21.2101C15.25 21.6801 14.23 21.4001 13.76 20.6101L13.65 20.4201C12.75 18.8501 11.27 18.8501 10.36 20.4201L10.25 20.6101C9.78 21.4001 8.76 21.6801 7.97 21.2101L6.24 20.2201C5.33 19.7001 5.02 18.5301 5.54 17.6301C6.45 16.0601 5.71 14.7801 3.9 14.7801C2.85 14.7801 2 13.9201 2 12.8801Z'
                        fill='#516F93'
                      />
                      <path
                        d='M12 15.25C13.7949 15.25 15.25 13.7949 15.25 12C15.25 10.2051 13.7949 8.75 12 8.75C10.2051 8.75 8.75 10.2051 8.75 12C8.75 13.7949 10.2051 15.25 12 15.25Z'
                        fill='#516F93'
                      />
                    </svg>
                    <span>Paystack Settlement</span>
                  </NavLink>
                </li>
              )}
            </div>
          )}
        </ul>
        <ul className='nav flex-column bottom-segment'>
          <li className='nav-item h-100'>
            <div className='profile-section w-100 position-relative' ref={dropdownRef}>
              <div
                className='profile-trigger'
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              >
                <div className='profile-avatar'>
                  <img src={profile} alt='Profile' className='avatar-image' />
                </div>
                <div className='profile-info'>
                  <div className='profile-name'>{user?.user.name ?? user?.user.username}</div>
                  <div className='profile-role'>{user?.user.name ? 'Super Admin' : 'Admin Access'}</div>
                </div>
                <div className={`dropdown-arrow ${isProfileDropdownOpen ? 'open' : ''}`}>
                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                    <path d='M4 6L8 10L12 6' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                  </svg>
                </div>
              </div>

              {isProfileDropdownOpen && (
                <div className='profile-dropdown'>
                  <div className='dropdown-item' onClick={handleLogout}>
                    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                      <path d='M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9' stroke='#516F93' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                      <path d='M16 17L21 12L16 7' stroke='#516F93' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                      <path d='M21 12H9' stroke='#516F93' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                    <span>Logout</span>
                  </div>
                </div>
              )}
            </div>
          </li>
        </ul>
      </div>
    </React.Fragment>
  )
}

export default AdminSideBarContent

